import { RouterLink } from '@/common/components/router-links/RouterLink';
import { Trans } from '@lingui/react/macro';
import { z } from 'zod';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { requiredInput } from '@/common/form/requiredInput';
import { FormSubmit } from '@/common/components/form-submit/FormSubmit';
import { FormErrors } from '@/common/components/form-errors/FormErrors';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { AccountService } from '@/features/auth/services/AccountService';
import { userQueryKeys } from '@/features/auth/queries';

const loginSchema = z.object({
  email: requiredInput(z.email()),
  password: requiredInput(z.string().min(1)),
});

interface LoginPageProps {
  redirectUri?: string;
}

export const LoginPage = ({ redirectUri }: LoginPageProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useAppForm(
    appFormOptions({
      schema: loginSchema,
      defaultValues: { email: null, password: null },
      onSubmit: async ({ value }) => {
        await AccountService.login(value);
        await queryClient.invalidateQueries({ queryKey: userQueryKeys.current() });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await navigate({ to: (redirectUri?.startsWith('/') ? redirectUri : '/') as any });
      },
    })
  );

  return (
    <>
      <div className="mb-8 flex flex-col items-center gap-2">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          <Trans>Welcome back</Trans>
        </h1>
        <p className="text-sm text-gray-500">
          <Trans>Sign in to your account</Trans>
        </p>
      </div>

      <form.AppForm>
        <form.FormElement>
          <FormErrors className="mb-4" />

          <div className="space-y-4">
            <form.AppField name="email">
              {(field) => (
                <field.TextField
                  label={<Trans>Email</Trans>}
                  type="email"
                  placeholder="name@company.com"
                />
              )}
            </form.AppField>

            <form.AppField name="password">
              {(field) => (
                <field.TextField
                  label={<Trans>Password</Trans>}
                  labelAddon={
                    <RouterLink to={'/forgot-password'} className="text-sm">
                      <Trans>Forgot password?</Trans>
                    </RouterLink>
                  }
                  type="password"
                />
              )}
            </form.AppField>
          </div>

          <FormSubmit className="mt-6 w-full">
            <Trans>Sign in</Trans>
          </FormSubmit>
        </form.FormElement>
      </form.AppForm>

      <p className="mt-6 text-center text-sm text-gray-500">
        <Trans>Don&apos;t have an account?</Trans>{' '}
        <RouterLink to={'/sign-up'}>
          <Trans>Sign up</Trans>
        </RouterLink>
      </p>
    </>
  );
};
